/**
 * trackServerEvent — single, memory-safe utility for ALL server-side event tracking.
 *
 * IMPORTANT: All event tracking to /api/events/track MUST go through this
 * function. Do NOT use raw fetch() for analytics — it risks Chromium crashes
 * in memory-constrained environments (64MB /dev/shm Docker containers).
 *
 * Uses navigator.sendBeacon (W3C standard for analytics — fire-and-forget,
 * minimal memory, survives page unloads). Falls back to plain fetch WITHOUT
 * keepalive.
 *
 * Usage:
 *   import { trackServerEvent } from '../utils/trackServerEvent';
 *   trackServerEvent('cta_ab_click', { test: 'my_test', variant: 'a' });
 */

let _visitorId = null;

const getVisitorId = () => {
  if (_visitorId) return _visitorId;
  try {
    // Lazy-import to avoid circular deps
    const { getOrCreateVisitorId } = require('./abTest');
    _visitorId = getOrCreateVisitorId();
    return _visitorId;
  } catch (_e) {
    return null;
  }
};

export const trackServerEvent = (eventType, metadata = {}) => {
  try {
    // Skip all tracking for bots/headless — prevents memory pressure
    if (window.__IS_BOT) return;

    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const url = `${apiBase}/api/events/track`;
    const payload = JSON.stringify({
      event_type: eventType,
      metadata: { ...metadata, visitor_id: metadata.visitor_id || getVisitorId() },
    });

    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return;
    }

    // Fallback: plain fetch WITHOUT keepalive
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: payload,
    }).catch(() => {});
  } catch (_e) { /* analytics must never crash the app */ }
};

export default trackServerEvent;
